// App
import React from 'react';

// Components
import Item from './Item';

// Misc / utils
import isEqual from 'lodash/isEqual';

// Types
import type ComparablesModelType from '../models/Comparables';
type RefsObjectType = {
  [key: string]: React.RefObject<HTMLDivElement>;
};

type MemoizedWrapperPropTypes = {
  comparables: Array<ComparablesModelType>;
  itemRefs: RefsObjectType;
};

const areEqual = (prevProps: MemoizedWrapperPropTypes, nextProps: MemoizedWrapperPropTypes) => {
  if (isEqual(prevProps.itemRefs, nextProps.itemRefs) && isEqual(prevProps.comparables, nextProps.comparables)) {
    return true;
  }

  return false;
};

/**
 * MemoizedWrapper's sole purpose is to memoize the comparables map. Prior to using this
 * memoized wrapper, the `itemRefs` object would cause the map to re-run and cause the
 * <Item /> component to re-render. The `areEqual` function checks for changes in `itemRefs`.
 *
 * From React docs:
 * "Conveniently, useMemo also lets you skip an expensive re-render of a child. Note that
 * this approach won’t work in a loop because Hook calls can’t be placed inside loops. But
 * you can extract a separate component for the list item, and call useMemo there."
 * @see https://reactjs.org/docs/hooks-faq.html#how-to-memoize-calculations
 */
const MemoizedWrapper = React.memo(({ comparables, itemRefs }: MemoizedWrapperPropTypes) => {
  return (
    <>
      {comparables &&
        comparables.map((comp, idx) => {
          const id = comp.listingId;

          return <Item id={id} item={comp} itemRef={itemRefs[id || '']} key={`carousel-item-${id}-${idx}`} idx={idx} />;
        })}
    </>
  );
}, areEqual);

MemoizedWrapper.displayName = 'MemoizedWrapper';

export default MemoizedWrapper;
